/**
 * Create List Modal
 * Modal for creating new shopping lists with templates
 * 
 * STANDALONE VERSION: Shopping List is now a standalone feature.
 * No budget/envelope integration - just simple list creation.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, ChevronRight } from 'lucide-react';
import { useAuth } from "@/contexts/AuthContext";
import { createShoppingList, createFromTemplate, LIST_TEMPLATES } from '@/services/shoppingListService';
import { findCategoryById } from '@/constants/unifiedCategories';
import logger from '@/utils/logger.js';
import UnifiedCategorySelector from '@/components/UnifiedCategorySelector';

const ICONS = [
  // Shopping & Groceries
  '🛒', '🛍️', '🏪', '🏬', '🧺', '📝', '🗒️', '✅', 
  // Food & Cooking
  '🍎', '🥕', '🥦', '🍞', '🧀', '🥛', '🥩', '🍗', '🐟', '🍕', '🍔', '🌮', '🍱', '🥗', '🍜',
  // Drinks
  '☕', '🧃', '🥤', '🍷', '🍺', '🧋',
  // Household & Cleaning
  '🏠', '🧹', '🧺', '🧼', '🧽', '🧴', '🚿', '🛁', '🧻', '💡', '🔌', '🔧', '🔨',
  // Health & Beauty
  '💊', '💉', '🏥', '💄', '💅', '🧴', '🪒', '🦷', '🧖',
  // Baby & Kids
  '👶', '🍼', '🧸', '🎨', '✏️', '📚', '🎒',
  // Clothing & Accessories
  '👕', '👔', '👗', '👠', '👟', '🧥', '🧦', '👓', '👜', '🎒',
  // Pets
  '🐕', '🐈', '🐾', '🦴', '🐟',
  // Electronics & Tech
  '📱', '💻', '🖨️', '🔋', '💾', '🎮', '📷', '🎧',
  // Party & Events
  '🎉', '🎊', '🎈', '🎁', '🎂', '🕯️', '🎆', '🎄', '🎃', '💐',
  // Sports & Fitness
  '🏋️', '⚽', '🏀', '🎾', '🏊', '🚴', '🧘', '⛹️',
  // Garden & Outdoors
  '🌿', '🌱', '🌻', '🌹', '🪴', '🌳', '🏡', '⛺', '🔥',
  // Car & Transport
  '🚗', '⛽', '🔧', '🚙', '🏍️',
  // Office & School
  '📋', '📌', '✂️', '📎', '🖊️', '✏️', '📏', '📐', '🗂️', '📁', '💼',
  // Special Occasions
  '💝', '💍', '🎀', '👰', '🤵', '💒',
  // General
  '⭐', '💫', '✨', '🌈', '❤️', '💚', '💙', '🔴', '🟢', '🔵', '⚫', '⚪'
];

const COLORS = [
  { name: 'Teal', value: '#006D77' },
  { name: 'Green', value: '#06D6A0' },
  { name: 'Blue', value: '#118AB2' },
  { name: 'Coral', value: '#EF476F' },
  { name: 'Yellow', value: '#FFD60A' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Orange', value: '#F97316' },
  { name: 'Pink', value: '#EC4899' }
];

export default function CreateListModal({ onClose, onSuccess, initialStep = 1 }) {
  const { user } = useAuth();
  
  const [step, setStep] = useState(initialStep); // 1: Template selection, 2: Customization
  const [selectedTemplate, setSelectedTemplate] = useState(initialStep === 2 ? 'blank' : null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('🛒');
  const [color, setColor] = useState('#006D77');
  const [loading, setLoading] = useState(false);
  const [showAllIcons, setShowAllIcons] = useState(false);
  
  // Unified category state for the list
  // Default to 'groceries-food' which is the canonical expense category ID
  const [categoryState, setCategoryState] = useState({
    categoryId: 'groceries-food',
    subcategoryId: null,
    customCategory: "",
    customSubcategory: ""
  });

  const templates = Object.values(LIST_TEMPLATES);

  // Top 16 most commonly used shopping list icons
  const TOP_ICONS = [
    '🛒', '🛍️', '🍎', '🥕', '🍞', '🥛', '🍕', '🏠',
    '🧹', '💊', '👕', '🐕', '🎉', '🏋️', '🚗', '📝'
  ];

  // Icons to display (top 16 or all)
  const displayedIcons = showAllIcons ? ICONS : TOP_ICONS;

  async function handleCreate() {
    if (!name.trim()) {
      alert('Please enter a list name');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        name: name.trim(),
        description: description.trim(),
        icon,
        color,
        // Unified category fields for the list
        categoryId: categoryState.categoryId || null,
        subcategoryId: categoryState.subcategoryId || null,
        customCategory: categoryState.customCategory || null,
        customSubcategory: categoryState.customSubcategory || null
      };

      if (selectedTemplate && selectedTemplate !== 'blank') {
        // Create from template
        await createFromTemplate(user.uid, selectedTemplate, payload);
      } else {
        // Create blank list
        await createShoppingList(user.uid, payload);
      }

      onSuccess();
    } catch (error) {
      logger.error('Error creating list', {
        message: error?.message,
        code: error?.code,
        stack: error?.stack
      });
      alert(
        `Failed to create list: ${
          error?.message || 'Unknown error. Please check console logs and try again.'
        }`
      );
    } finally {
      setLoading(false);
    }
  }

  function handleTemplateSelect(template) {
    setSelectedTemplate(template.id);
    setName(template.name);
    setIcon(template.icon);
    setColor(template.color);
    setStep(2);
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.95, y: 20 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#F9FBFB] dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-[#F9FBFB] dark:bg-slate-800 border-b-2 border-gray-200 dark:border-slate-600 p-5 sm:p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {step === 1 ? 'Choose a Template' : 'Customize Your List'}
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {step === 1 
                ? 'Start with a template or create from scratch' 
                : 'Add details to your shopping list'
              }
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white dark:hover:bg-slate-700 rounded-xl transition-colors text-gray-900 dark:text-white"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="p-5 sm:p-6">
          {step === 1 ? (
            /* Step 1: Template Selection */
            <div className="space-y-4">
              {templates.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  onClick={() => handleTemplateSelect(template)}
                  className="bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-2xl p-5 cursor-pointer
                             hover:border-[#006D77] dark:hover:border-[#06D6A0] hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                      <div 
                        className="w-14 h-14 rounded-xl flex items-center justify-center text-3xl"
                        style={{ backgroundColor: `${template.color}20` }}
                      >
                        {template.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-gray-900 dark:text-white mb-1">
                          {template.name}
                        </h3>
                        {template.categories.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {template.categories.slice(0, 3).map((cat, idx) => (
                              <span 
                                key={idx}
                                className="text-xs px-2 py-1 bg-gray-100 dark:bg-slate-600 rounded-lg text-gray-600 dark:text-gray-300"
                              >
                                {cat.icon} {cat.name}
                              </span>
                            ))}
                            {template.categories.length > 3 && (
                              <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-slate-600 rounded-lg text-gray-600 dark:text-gray-300">
                                +{template.categories.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                        {template.id === 'blank' && (
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Start with a clean slate
                          </p>
                        )}
                      </div>
                    </div>
                    <ChevronRight size={24} className="text-gray-400 dark:text-gray-500" />
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            /* Step 2: Customization */
            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  List Name <span className="text-[#EF476F]">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Weekly Groceries"
                  className="w-full px-4 py-3.5 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-2xl
                             focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10
                             text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all"
                  autoFocus
                />
              </div>

              {/* Category Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Category
                </label>
                <UnifiedCategorySelector
                  module="shopping"
                  value={categoryState}
                  onChange={(newState) => {
                    setCategoryState(newState);
                    
                    // Auto-fill name and description from category (if empty)
                    if (newState.categoryId || newState.customCategory) {
                      const category = findCategoryById(newState.categoryId);
                      
                      // Auto-fill name if empty
                      if (!name.trim()) {
                        let autoName = '';
                        if (newState.customCategory) {
                          autoName = `${newState.customCategory} List`;
                        } else if (category?.name) {
                          autoName = `${category.name} List`;
                        }
                        if (autoName) {
                          setName(autoName);
                        }
                      }
                      
                      // Auto-fill description if empty
                      if (!description.trim() && category?.description) {
                        setDescription(category.description);
                      }
                    }
                  }}
                  allowSubcategory={false}
                  allowCustom={true}
                  placeholder="Select list category"
                />
                <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                  Choose a category to help organize your list and improve insights.
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Description <span className="text-gray-400 dark:text-gray-500 text-xs">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Add a description..."
                  className="w-full px-4 py-3.5 bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 rounded-2xl
                             focus:outline-none focus:border-[#006D77] focus:ring-4 focus:ring-[#006D77]/10
                             text-base text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all"
                />
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Icon
                </label>
                <div className="grid grid-cols-6 sm:grid-cols-8 gap-2">
                  {displayedIcons.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => setIcon(emoji)}
                      className={`
                        aspect-square rounded-xl text-2xl flex items-center justify-center
                        transition-all duration-200 hover:scale-110
                        ${icon === emoji 
                          ? 'bg-[#006D77] shadow-lg scale-110' 
                          : 'bg-white dark:bg-slate-700 border-2 border-gray-200 dark:border-slate-600 hover:border-[#006D77]'
                        }
                      `}
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
                
                {/* Show More/Less Icons Button */}
                <button
                  type="button"
                  onClick={() => setShowAllIcons(!showAllIcons)}
                  className="mt-3 w-full py-2.5 px-4 text-sm font-medium text-[#006D77] dark:text-[#06D6A0]
                             bg-[#006D77]/5 dark:bg-[#006D77]/10 hover:bg-[#006D77]/10 dark:hover:bg-[#006D77]/20 rounded-xl
                             border-2 border-dashed border-[#006D77]/30 hover:border-[#006D77]/50
                             transition-all duration-200 flex items-center justify-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  {showAllIcons ? 'See less' : 'Click here for more icons'}
                </button>
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Color Theme
                </label>
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c.value}
                      onClick={() => setColor(c.value)}
                      className={`
                        aspect-square rounded-xl transition-all duration-200
                        ${color === c.value 
                          ? 'ring-4 ring-offset-2 scale-110' 
                          : 'hover:scale-110'
                        }
                      `}
                      style={{ 
                        backgroundColor: c.value,
                        ringColor: c.value
                      }}
                      title={c.name}
                    />
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 px-6 py-4 bg-white dark:bg-slate-700 border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-200
                             rounded-2xl hover:bg-gray-50 dark:hover:bg-slate-600 transition-all font-semibold"
                >
                  Back
                </button>
                <button
                  onClick={handleCreate}
                  disabled={loading || !name.trim()}
                  className={`
                    flex-1 px-6 py-4 bg-[#006D77] text-white rounded-2xl font-bold
                    shadow-lg hover:shadow-xl hover:bg-[#005661] transition-all
                    flex items-center justify-center gap-2
                    ${(loading || !name.trim()) ? 'opacity-50 cursor-not-allowed' : ''}
                  `}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Sparkles size={20} />
                      Create List
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
